import { useUIStore, type Theme, type ResolvedTheme } from '@/stores/ui.store'

/**
 * 主题 Hook 返回值
 */
interface UseThemeReturn {
  /** 当前主题设置 (light | dark | system) */
  theme: Theme
  /** 解析后的主题（实际应用的主题） */
  resolvedTheme: ResolvedTheme
  /** 设置主题 */
  setTheme: (theme: Theme) => void
  /** 切换主题（在 light/dark 之间循环） */
  toggleTheme: () => void
  /** 是否为暗色模式 */
  isDark: boolean
}

/**
 * 主题 Hook
 * 
 * 提供主题状态访问和切换功能
 * 
 * @example
 * ```tsx
 * const { theme, resolvedTheme, setTheme, toggleTheme, isDark } = useTheme()
 * 
 * // 设置主题
 * setTheme('dark')
 * 
 * // 切换主题
 * toggleTheme()
 * 
 * // 检查是否为暗色模式
 * if (isDark) {
 *   // ...
 * }
 * ```
 */
export function useTheme(): UseThemeReturn {
  const theme = useUIStore((state) => state.theme)
  const systemTheme = useUIStore((state) => state.systemTheme)
  const setTheme = useUIStore((state) => state.setTheme)
  const toggleTheme = useUIStore((state) => state.toggleTheme)

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme
  const isDark = resolvedTheme === 'dark'

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark,
  }
}
